import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquareCode, Gamepad2, Radar, GitBranch } from 'lucide-react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from './ui/resizable';
import VersionTree from './warroom/VersionTree';
import { useJob, isActive } from './JobBadge';
import { toast } from 'sonner';
import { useLocalProgress } from '../lib/progress';
import ArchitectChat from './ArchitectChat';
import Simulator from './Simulator';
import WarRoom from './WarRoom';
import { bus, capture, vibrate } from '../lib/bus';
import { buildGameDoc } from '../lib/runtime';
import { detectControls } from '../lib/analyze';
import { addScore } from '../lib/store';
import { makePoster } from '../lib/poster';

const TABS = [
  { id: 'chat', label: 'Architetto', icon: MessageSquareCode },
  { id: 'sim', label: 'Gioca', icon: Gamepad2 },
  { id: 'versions', label: 'Versioni', icon: GitBranch },
  { id: 'war', label: 'War Room', icon: Radar },
];

function useDesktop() {
  const q = '(min-width: 1024px)';
  const [d, setD] = useState(() => window.matchMedia(q).matches);
  useEffect(() => { const m = window.matchMedia(q); const f = () => setD(m.matches); m.addEventListener('change', f); return () => m.removeEventListener('change', f); }, []);
  return d;
}

export default function Studio({ game, settings, updateSettings, updateGame, command, clearCommand, onClone }) {
  const [tab, setTab] = useState(command ? 'chat' : 'sim');
  const [side, setSide] = useState('war');
  const desktop = useDesktop();
  const { job, meta } = useJob();
  const [reloadKey, setReloadKey] = useState(0);
  const [padMode, setPadMode] = useState('auto');
  const [stats, setStats] = useState({ fps: 0, mem: null, entities: 0, hist: [] });
  const [paramDefs, setParamDefs] = useState({});
  const [paramVals, setParamVals] = useState({});
  const [errors, setErrors] = useState([]);
  const [liveScore, setLiveScore] = useState(0);
  const [cmd, setCmd] = useState(command);
  const best = useRef(game.scores?.[0]?.value ?? -1);
  const detected = useMemo(() => detectControls(game.code), [game.code]);
  const controls = padMode === 'auto' ? detected : padMode;

  const cfg = useMemo(() => ({ fps: settings.fps, controls, showPad: settings.showPad, master: settings.master, sfx: settings.sfx, music: settings.music, bpm: settings.bpm, musicOn: settings.musicOn, haptics: settings.haptics }), [settings, controls]);
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;
  const progress = useLocalProgress(`vault:${game.id}`);
  useEffect(() => bus.on((m) => {
    if (m.type === 'sdk' && m.action === 'shop') toast('Negozio in arrivo', { id: 'sdk-shop', description: 'Nello Store il gioco mostrerà lo stesso avviso finché i pagamenti non sono attivi.' });
    else if (m.type === 'sdk' && m.action === 'login') toast('Login richiesto dal gioco', { id: 'sdk-login', description: 'Nello Store si apre la finestra di accesso del giocatore.' });
  }), []);
  const srcDoc = useMemo(() => (progress.loaded ? buildGameDoc(game.code, { ...cfgRef.current, storage: progress.get() }) : ''), [game.code, reloadKey, progress.loaded]); // eslint-disable-line

  useEffect(() => { bus.post({ type: 'cfg', cfg }); }, [cfg]);
  useEffect(() => { bus.post({ type: 'cfg', cfg: cfgRef.current, rebuildPad: true }); }, [controls, settings.showPad]);
  useEffect(() => { setErrors([]); setParamDefs({}); }, [srcDoc]);

  useEffect(() => bus.on((m) => {
    if (m.type === 'ready') bus.post({ type: 'cfg', cfg: cfgRef.current });
    else if (m.type === 'stats') setStats((s) => ({ ...m, hist: [...s.hist.slice(-59), m.fps] }));
    else if (m.type === 'params') { setParamDefs(m.defs); setParamVals(Object.fromEntries(Object.entries(m.defs).map(([k, d]) => [k, d.v]))); }
    else if (m.type === 'error') setErrors((e) => [...e.slice(-9), { msg: m.msg, line: m.line, t: Date.now() }]);
    else if (m.type === 'haptic') vibrate(m.p, cfgRef.current.haptics);
    else if (m.type === 'score') setLiveScore(m.value);
    else if (m.type === 'gameover') {
      if (m.value > best.current && m.value > 0) { toast.success(`Nuovo record: ${m.value}`); best.current = m.value; }
      updateGame((g) => addScore(g, m.value));
    }
  }), [updateGame]);

  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && stats.fps > 0) updateGame((g) => ({ ...g, playTime: (g.playTime || 0) + 10 }));
    }, 10000);
    return () => clearInterval(t);
  }, [stats.fps > 0]); // eslint-disable-line

  useEffect(() => { if (command) clearCommand(); }, []); // eslint-disable-line

  const setParam = (key, value) => { setParamVals((v) => ({ ...v, [key]: value })); bus.post({ type: 'param', key, value }); };
  const resetParams = () => Object.entries(paramDefs).forEach(([k, d]) => setParam(k, d.v));

  const shootCover = async () => {
    const frame = await capture();
    if (!frame) toast.message('Nessun canvas catturabile: copertina generata proceduralmente');
    const cover = await makePoster({ title: game.title, genre: game.genre, frame });
    updateGame((g) => ({ ...g, cover }));
    toast.success('Locandina 16:9 generata e salvata nel Vault');
  };
  const runAi = (c) => { setCmd({ id: Date.now(), ...c }); setTab('chat'); };

  const shared = { game, settings, updateSettings, updateGame };
  const chat = <ArchitectChat {...shared} command={cmd} onApplied={() => { setReloadKey((k) => k + 1); setTab((t) => (window.innerWidth < 1024 ? 'sim' : t)); }} />;
  const sim = <Simulator {...shared} srcDoc={srcDoc} reloadKey={reloadKey} reload={() => setReloadKey((k) => k + 1)} padMode={padMode} setPadMode={setPadMode}
    detected={detected} errors={errors} clearErrors={() => setErrors([])} onFix={(msg) => runAi({ mode: 'fix', prompt: msg })} onCapture={shootCover} stats={stats} liveScore={liveScore} />;
  const war = <WarRoom {...shared} stats={stats} paramDefs={paramDefs} paramVals={paramVals} setParam={setParam} resetParams={resetParams} onCapture={shootCover} onClone={onClone}
    onCheckout={() => setReloadKey((k) => k + 1)} />;
  const versions = (
    <div data-testid="studio-versions" className="glass lg:rounded-2xl h-full min-h-0 overflow-y-auto p-3">
      <div className="t-label mb-2 px-1">Cronologia versioni · tocca “Torna qui” per ripristinare</div>
      <VersionTree {...shared} onClone={onClone} onCheckout={() => setReloadKey((k) => k + 1)} />
    </div>
  );
  const pct = isActive(job) && meta?.gameId === game.id ? job.progress_percent || 0 : null;

  if (desktop) {
    return (
      <div data-testid="studio-view" className="h-full p-3">
        <ResizablePanelGroup direction="horizontal" autoSaveId="nexora.studio.layout" className="h-full">
          <ResizablePanel defaultSize={30} minSize={22} className="min-h-0 flex flex-col">{chat}</ResizablePanel>
          <ResizableHandle withHandle data-testid="studio-resize-1" className="mx-1.5 bg-transparent" />
          <ResizablePanel defaultSize={44} minSize={28} className="min-h-0 flex flex-col">{sim}</ResizablePanel>
          <ResizableHandle withHandle data-testid="studio-resize-2" className="mx-1.5 bg-transparent" />
          <ResizablePanel defaultSize={26} minSize={18} className="min-h-0 flex flex-col gap-2">
            <div className="flex gap-1 rounded-lg bg-black/30 p-0.5 text-[11px] font-bold">
              {[['war', 'War Room'], ['versions', 'Versioni']].map(([id, l]) => (
                <button key={id} data-testid={`studio-side-${id}`} onClick={() => setSide(id)} className={`flex-1 rounded-md py-1.5 transition-colors ${side === id ? 'bg-violet-500/25 text-violet-100' : 'text-slate-400 hover:text-slate-200'}`}>{l}</button>
              ))}
            </div>
            <div className="flex-1 min-h-0 flex flex-col">{side === 'war' ? war : versions}</div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }
  return (
    <div data-testid="studio-view" className="h-full flex flex-col">
      {[['chat', chat], ['sim', sim], ['versions', versions], ['war', war]].map(([id, el]) => (
        <section key={id} className={`${tab === id ? 'flex' : 'hidden'} flex-1 min-h-0 flex-col`}>{el}</section>
      ))}
      <nav className="grid grid-cols-4 border-t border-violet-500/20 bg-titan-bg/90 backdrop-blur-xl pb-safe">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`studio-tab-${t.id}`} onClick={() => { setTab(t.id); vibrate(8, settings.haptics); }}
            className={`relative flex flex-col items-center gap-1 py-3 text-[11px] font-semibold tracking-wide transition-colors active:scale-95 ${tab === t.id ? 'text-cyan-300' : 'text-slate-500'}`}>
            {tab === t.id && <span className="absolute top-0 h-0.5 w-10 rounded-full bg-cyan-400" />}
            <t.icon className="h-6 w-6" />{t.label}
            {t.id === 'chat' && pct !== null && <span data-testid="studio-tab-chat-progress" className="absolute top-1.5 right-[22%] rounded-full bg-cyan-500 px-1.5 text-[9px] font-black text-black">{pct}%</span>}
          </button>
        ))}
      </nav>
    </div>
  );
}
